/**
 * Logging levels in order of verbosity
 */
export enum LogLevel {
  DEBUG = 0,   // Most verbose: logs everything
  NETWORK = 1, // Logs network requests, warnings, errors, and basic info
  INFO = 2,    // Logs basic info, warnings, and errors
  WARN = 3,    // Logs warnings and errors
  ERROR = 4,   // Logs only errors
  NONE = 5     // No logging at all
}

/**
 * Environment configuration for the logger
 */
interface LoggerConfig {
  level: LogLevel;
  enabled: boolean;
}

/**
 * Get the configured log level from environment variables
 */
function getLogConfig(): LoggerConfig {
  // Default to INFO in production, DEBUG in development
  const isProd = import.meta.env.PROD;
  const defaultLevel = isProd ? LogLevel.INFO : LogLevel.DEBUG;
  
  // Get log level from environment variable
  const configuredLevel = (import.meta.env.VITE_LOG_LEVEL || '').toUpperCase();
  
  // Map string to LogLevel enum
  let level: LogLevel;
  switch (configuredLevel) {
    case 'DEBUG':
      level = LogLevel.DEBUG;
      break;
    case 'NETWORK':
      level = LogLevel.NETWORK;
      break;
    case 'INFO':
      level = LogLevel.INFO;
      break;
    case 'WARN':
      level = LogLevel.WARN;
      break;
    case 'ERROR':
      level = LogLevel.ERROR;
      break;
    case 'NONE':
      level = LogLevel.NONE;
      break;
    default:
      level = defaultLevel;
  }
  
  // Check if logging is enabled entirely
  const enabled = import.meta.env.VITE_LOGGING_ENABLED !== 'false';
  
  return { level, enabled };
}

// Initialize logger configuration
const config = getLogConfig();

/**
 * Logger class for consistent logging with level control
 */
class Logger {
  private static instance: Logger;
  private logConfig: LoggerConfig;
  
  private constructor() {
    this.logConfig = config;
  }
  
  /**
   * Get the singleton logger instance
   */
  public static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }
  
  /**
   * Update logger configuration at runtime
   */
  public setConfig(config: Partial<LoggerConfig>): void {
    this.logConfig = { ...this.logConfig, ...config };
  }
  
  /**
   * Log a debug message (most verbose)
   */
  public debug(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.log(`[DEBUG] ${message}`, ...args);
    }
  }
  
  /**
   * Log network-related information
   */
  public network(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.NETWORK)) {
      console.log(`[NETWORK] ${message}`, ...args);
    }
  }
  
  /**
   * Log general information
   */
  public info(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.log(`[INFO] ${message}`, ...args);
    }
  }
  
  /**
   * Log warnings
   */
  public warn(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(`[WARN] ${message}`, ...args);
    }
  }
  
  /**
   * Log errors
   */
  public error(message: string, ...args: any[]): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      console.error(`[ERROR] ${message}`, ...args);
    }
  }
  
  /**
   * Check if we should log at the specified level
   */
  private shouldLog(level: LogLevel): boolean {
    return this.logConfig.enabled && level >= this.logConfig.level;
  }
}

// Export a singleton instance
export const logger = Logger.getInstance();

// Export simpler named exports for convenience
export const debug = (...args: Parameters<typeof logger.debug>) => logger.debug(...args);
export const network = (...args: Parameters<typeof logger.network>) => logger.network(...args);
export const info = (...args: Parameters<typeof logger.info>) => logger.info(...args);
export const warn = (...args: Parameters<typeof logger.warn>) => logger.warn(...args);
export const error = (...args: Parameters<typeof logger.error>) => logger.error(...args); 